/**
 * QrPreviewCard Component
 *
 * A reusable card component for displaying a single QR preview with a label beneath.
 * Handles loading, error, and empty states with appropriate visual feedback.
 * Uses ShieldSans font for labels per typography guidelines.
 *
 * Requirements:
 * - 1.6: Display a label beneath each QR code indicating its type
 * - 7.1: Display a loading indicator for that specific preview while generating
 * - 7.2: Display an error state for that specific preview if generation fails
 */

import './QrPreviewCard.css';

/**
 * Props for the QrPreviewCard component
 */
export interface QrPreviewCardProps {
  /** Label text displayed beneath the QR code (e.g., "Site", "Bay 1", "VIP Lounge") */
  readonly label: string;
  /** Data URL of the generated QR code image, null if not yet generated */
  readonly dataUrl: string | null;
  /** Whether QR code generation is in progress */
  readonly isGenerating: boolean;
  /** Error message if generation failed, null otherwise */
  readonly error: string | null;
  /** The URL encoded in the QR code, displayed as a link */
  readonly url?: string | null;
  /** Callback to download this QR code, if provided shows download button */
  readonly onDownload?: () => void;
  /** Callback when the QR code image is clicked, for opening in a dialog */
  readonly onClick?: () => void;
}

/**
 * Loading spinner component for the generating state
 */
function LoadingSpinner(): JSX.Element {
  return (
    <div className="qr-preview-card__spinner" role="status" aria-label="Generating QR code">
      <svg
        className="qr-preview-card__spinner-icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          className="qr-preview-card__spinner-track"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
        />
        <path
          className="qr-preview-card__spinner-arc"
          d="M12 2a10 10 0 0 1 10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      <span className="qr-preview-card__spinner-text">Generating...</span>
    </div>
  );
}

/**
 * Error display component for the error state
 */
function ErrorDisplay({ message }: { readonly message: string }): JSX.Element {
  return (
    <div className="qr-preview-card__error" role="alert">
      <svg
        className="qr-preview-card__error-icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="16" r="1" fill="currentColor" />
      </svg>
      <span className="qr-preview-card__error-title">Failed</span>
      <span className="qr-preview-card__error-message">{message}</span>
    </div>
  );
}

/**
 * Placeholder display for the empty state (no QR code generated yet)
 */
function EmptyPlaceholder(): JSX.Element {
  return (
    <div className="qr-preview-card__placeholder">
      <svg
        className="qr-preview-card__placeholder-icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* QR code icon representation */}
        <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" />
        <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" />
        <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" />
        <rect x="14" y="14" width="3" height="3" stroke="currentColor" strokeWidth="2" />
        <rect x="18" y="14" width="3" height="3" stroke="currentColor" strokeWidth="2" />
        <rect x="14" y="18" width="3" height="3" stroke="currentColor" strokeWidth="2" />
        <rect x="18" y="18" width="3" height="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    </div>
  );
}

/**
 * Download icon SVG component
 */
function DownloadIcon(): JSX.Element {
  return (
    <svg
      className="qr-preview-card__download-icon"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="7 10 12 15 17 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="15"
        x2="12"
        y2="3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * QrPreviewCard - Displays a single QR code preview with label
 *
 * A card component designed for use in the multi-preview layout. Each card
 * displays a QR code image with a label beneath indicating its type.
 *
 * Handles four states:
 * 1. Loading state (isGenerating = true): Shows spinner
 * 2. Error state (error !== null): Shows error message
 * 3. Success state (dataUrl !== null): Shows QR code image
 * 4. Empty state (dataUrl === null, not generating, no error): Shows placeholder
 *
 * @example
 * ```tsx
 * // Loading state
 * <QrPreviewCard label="Site" dataUrl={null} isGenerating={true} error={null} />
 *
 * // Success state
 * <QrPreviewCard label="Bay 1" dataUrl="data:image/png;base64,..." isGenerating={false} error={null} />
 *
 * // Error state
 * <QrPreviewCard label="VIP Lounge" dataUrl={null} isGenerating={false} error="Failed to generate" />
 *
 * // Empty state
 * <QrPreviewCard label="Site" dataUrl={null} isGenerating={false} error={null} />
 * ```
 */
export function QrPreviewCard({
  label,
  dataUrl,
  isGenerating,
  error,
  url,
  onDownload,
  onClick,
}: QrPreviewCardProps): JSX.Element {
  const isClickable = dataUrl !== null && !isGenerating && error === null && onClick !== undefined;

  // Handle click on the QR code container
  const handleContainerClick = (): void => {
    if (isClickable && onClick) {
      onClick();
    }
  };

  // Handle keyboard interaction for accessibility
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (isClickable && onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  // Determine which state to render
  const renderContent = (): JSX.Element => {
    // Loading state takes priority
    if (isGenerating) {
      return <LoadingSpinner />;
    }

    // Error state
    if (error !== null) {
      return <ErrorDisplay message={error} />;
    }

    // Success state - show QR code image
    if (dataUrl !== null) {
      return (
        <img
          className="qr-preview-card__image"
          src={dataUrl}
          alt={`QR code for ${label}`}
        />
      );
    }

    // Empty state - show placeholder
    return <EmptyPlaceholder />;
  };

  const canDownload = dataUrl !== null && !isGenerating && error === null && onDownload !== undefined;

  return (
    <div className="qr-preview-card">
      <div
        className={`qr-preview-card__container${isClickable ? ' qr-preview-card__container--clickable' : ''}`}
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-label={isClickable ? `View ${label} QR code in larger view` : undefined}
      >
        {renderContent()}
      </div>
      {url && (
        <a
          className="qr-preview-card__url"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={url}
        >
          {url}
        </a>
      )}
      <span className="qr-preview-card__label">{label}</span>
      {onDownload !== undefined && (
        <button
          type="button"
          className="qr-preview-card__download-button"
          onClick={onDownload}
          disabled={!canDownload}
          aria-label={`Download ${label} QR code`}
        >
          <DownloadIcon />
          <span>Download</span>
        </button>
      )}
    </div>
  );
}
